import { SizeType, FitType, LayoutType, SpeakerDirectionType } from '../types/asset-types';
import { BaseConfig } from '../types/speaker-types';
import {
  computeLandscapeLayout,
  computePortraitLayout,
  computeSquareLayout
} from '@goldcast/utils/src/media/speakerLayoutConfig';
import type {
  LayoutParams,
  LayoutResult,
  TileStyle,
  PipShape,
  PipPosition
} from '@goldcast/utils/src/media/speakerLayoutConfig';
import { getAspectRatio } from './sizeUtils';

// Generator layout kinds (subset of the generator's LayoutType)
type GeneratorKind = 'FILL' | 'TILES' | 'SHARED_FILL' | 'FILM_STRIP' | 'PIP';

interface ResolvedLayout {
  kind: GeneratorKind;
  speakerCount: number;
}

export function resolveNewLayoutKind(params: {
  layout: LayoutType;
  fitMode: FitType;
  totalVideoCount: number;
  hasScreenshare: boolean;
}): ResolvedLayout | null {
  const { layout, fitMode, totalVideoCount, hasScreenshare } = params;

  if (layout === 'AUDIOGRAM' || layout === 'DEFAULT') {
    return null;
  }
  if (layout === 'PIP') {
    return { kind: 'PIP', speakerCount: Math.min(2, totalVideoCount - 1) };
  }

  // A single-video overlap (screenshare-only or sole speaker) → FILL/TILES, 1 speaker.
  // Don't use ONLY_SCREEN here — portrait letterboxes by default; today's GRID/1 does not.
  if (totalVideoCount <= 1) {
    return { kind: fitMode === 'FILL' ? 'FILL' : 'TILES', speakerCount: 1 };
  }

  if (hasScreenshare) {
    if (layout === 'FOCUS') {
      return { kind: 'FILM_STRIP', speakerCount: totalVideoCount - 1 };
    }
    // GRID / SPEAKER + screenshare → SHARED_FILL (speaker panel on the side)
    return { kind: 'SHARED_FILL', speakerCount: totalVideoCount - 1 };
  }

  // GRID, SPEAKER, FOCUS without screenshare → FILL (fill=FILL) or TILES (fit=FIT)
  return { kind: fitMode === 'FILL' ? 'FILL' : 'TILES', speakerCount: totalVideoCount };
}

// ────────────────────────────────────────────────────────────────────────────
// (b) Adapter: TileStyle[] → BaseConfig[]
// ────────────────────────────────────────────────────────────────────────────

/**
 * Rewrite cqw/cqh units to %. The generator normalises against W=100, H=AR*100,
 * so the numeric value is already percent-of-container and the rewrite is 1:1.
 */
const toPct = (v: unknown): unknown => (typeof v === 'string' && /(cqw|cqh)$/.test(v) ? `${parseFloat(v)}%` : v);

function adaptContainerStyle(cs: TileStyle['containerStyle']): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cs)) {
    result[key] = toPct(value);
  }
  return result;
}

const DISPLAY_NONE: BaseConfig = {
  style: { display: 'none' } as BaseConfig['style'],
  containerStyle: { display: 'none' } as BaseConfig['containerStyle']
};

function adaptTilesToBaseConfig(tiles: TileStyle[], totalVideoCount: number): BaseConfig[] {
  const adapted: BaseConfig[] = tiles.map(tile => ({
    style: tile.style as BaseConfig['style'],
    containerStyle: adaptContainerStyle(tile.containerStyle) as BaseConfig['containerStyle']
  }));

  // Exact length: truncate or pad with display:none so finalConfig.length === totalVideoCount.
  // TemplateLayoutPreview.tsx:462 spreads containerStyle with no optional chaining — an
  // undefined entry at any index causes a runtime crash.
  const result = adapted.slice(0, totalVideoCount);
  while (result.length < totalVideoCount) {
    result.push(DISPLAY_NONE);
  }
  return result;
}

type ComputeFn = (params: LayoutParams) => LayoutResult;

const COMPUTE: Record<SizeType, ComputeFn> = {
  LANDSCAPE: computeLandscapeLayout,
  PORTRAIT: computePortraitLayout,
  SQUARE: computeSquareLayout
};

export function computeNewEngine(params: {
  size: SizeType;
  kind: GeneratorKind;
  speakerCount: number;
  totalVideoCount: number;
  hideBorders: boolean;
  roundCorners: boolean;
  speakerDirection?: SpeakerDirectionType;
  pipShape?: PipShape;
  pipPosition?: PipPosition;
  hasScreenshare?: boolean;
}): BaseConfig[] {
  const {
    size,
    kind,
    speakerCount,
    totalVideoCount,
    hideBorders,
    roundCorners,
    speakerDirection,
    pipShape,
    pipPosition,
    hasScreenshare = false
  } = params;

  const showBorders = !hideBorders;
  const roundedCorners = showBorders && roundCorners;

  const aspectRatio = 1 / getAspectRatio(size);

  const generatorParams: LayoutParams = {
    layout: kind,
    aspectRatio,
    speakerCount,
    showBorders,
    roundedCorners,
    pipShape: pipShape ?? 'RECTANGULAR',
    pipPosition: pipPosition ?? 'RIGHT_BOTTOM',
    speakerSide: speakerDirection ?? 'RIGHT',
    maintainScreenAspectRatio: kind === 'PIP' ? hasScreenshare : true
  };

  const result = COMPUTE[size](generatorParams);
  const configs = adaptTilesToBaseConfig(result.tiles, totalVideoCount);

  return configs;
}
